
import React, { useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';

interface TimeSelectProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

const TimeSelect: React.FC<TimeSelectProps> = ({
  label,
  options,
  value,
  onChange,
}) => {
  const { t } = useTranslationUtils();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Scroll to the selected item when component mounts or value changes
  useEffect(() => {
    if (selectedItemRef.current && containerRef.current) {
      const container = containerRef.current;
      const selectedItem = selectedItemRef.current;
      
      // Calculate the scroll position to center the selected item
      const scrollPosition = selectedItem.offsetTop - 
        (container.clientHeight / 2) + 
        (selectedItem.clientHeight / 2);
      
      container.scrollTop = scrollPosition;
    }
  }, [value]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, option: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(option);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentIndex = options.indexOf(option);
      if (currentIndex < options.length - 1) {
        onChange(options[currentIndex + 1]);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentIndex = options.indexOf(option);
      if (currentIndex > 0) {
        onChange(options[currentIndex - 1]);
      }
    }
  };

  // Function to clear selection
  const handleClear = () => {
    onChange('');
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{label}</p>
        {value && (
          <button 
            type="button"
            onClick={handleClear}
            className="text-xs text-muted-foreground hover:text-foreground"
            aria-label={t('common.clear', 'Clear')}
          >
            {t('common.clear', 'Clear')}
          </button>
        )}
      </div>
      <div 
        ref={containerRef}
        className="h-[200px] overflow-y-auto pr-2 border rounded-md"
        role="listbox"
        aria-label={label}
        tabIndex={0}
      >
        {options.map((option) => (
          <div
            key={option}
            ref={option === value ? selectedItemRef : null}
            className={cn(
              "py-1.5 px-3 cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-sm",
              value === option && "bg-primary text-primary-foreground"
            )}
            onClick={() => onChange(option)}
            role="option"
            aria-selected={value === option}
            tabIndex={0}
            onKeyDown={(e) => handleKeyDown(e, option)}
            data-value={option}
          >
            {option}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimeSelect;
